import { Link, type LinkProps } from '@tanstack/react-router';
import { Video, FileText, Trophy, AlertTriangle } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { HomeTodayEvent, HomeDueItem } from '@/lib/api/home';

/** Which group a row belongs to. Drives the dot colour and the subtitle copy. */
type Tone = 'overdue' | 'today' | 'upcoming';

const MS_PER_DAY = 86_400_000;
const MAX_OVERDUE_ROWS = 3;

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function daysOverdue(iso: string): number {
  return Math.round(
    (startOfDay(new Date()).getTime() - startOfDay(new Date(iso)).getTime()) / MS_PER_DAY,
  );
}

/** Missed, unsubmitted work — most recent first, so the still-submittable items lead. */
function overdueEvents(dueItems: HomeDueItem[]): HomeTodayEvent[] {
  return dueItems
    .filter((i) => i.state === 'overdue' && i.due_date)
    .sort((a, b) => (a.due_date! < b.due_date! ? 1 : -1))
    .slice(0, MAX_OVERDUE_ROWS)
    .map((i) => ({
      id: i.assessment_id,
      type: 'assignment',
      title: i.title,
      time: i.due_date!,
      batch_id: i.batch_id,
      batch_name: i.batch_name,
      topic_id: i.topic_id,
      category: i.category,
    }));
}

const KIND_LABEL: Record<string, string> = {
  session: 'Live session',
  contest: 'Contest',
  problems: 'Practice problem',
  quizzes: 'Quiz',
  assignments: 'Assignment',
  projects: 'Project',
};

function kindLabel(ev: HomeTodayEvent): string {
  if (ev.type === 'session' || ev.type === 'contest') return KIND_LABEL[ev.type];
  return KIND_LABEL[ev.category ?? ''] ?? 'Assignment';
}

/**
 * Where a row navigates. Routed off the event's *own* batch, never the course
 * picker's — under "All Courses" consecutive rows belong to different courses.
 * Returns null when the API didn't send enough to build a real target, so we
 * render a plain card instead of a link that 404s.
 */
function eventHref(ev: HomeTodayEvent): string | null {
  if (ev.type === 'contest') return '/contests';
  if (!ev.batch_id) return null;
  if (ev.type === 'session') {
    return ev.topic_id ? `/courses/${ev.batch_id}/topics/${ev.topic_id}` : null;
  }
  return ev.category ? `/courses/${ev.batch_id}/practice/${ev.category}/${ev.id}` : null;
}

function fmtFull(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * The gutter is 64px wide, so a full "Mon, Jul 27, 9:37 PM" can't live on one
 * line. Anything that isn't today gets a day line above the time line.
 */
function gutterLines(iso: string, tone: Tone): [string] | [string, string] {
  if (tone === 'today') return [fmtTime(iso)];

  const d = new Date(iso);
  const days = Math.abs(daysOverdue(iso));
  const day =
    days <= 6
      ? d.toLocaleDateString('en-US', { weekday: 'short' })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return [day, fmtTime(iso)];
}

function subtitle(ev: HomeTodayEvent, tone: Tone): string {
  if (tone === 'overdue') {
    const n = daysOverdue(ev.time);
    return n <= 1 ? 'Overdue by 1 day' : `Overdue by ${n} days`;
  }
  if (ev.type === 'session') return 'Live Session';
  if (ev.type === 'contest') return `Starts at ${fmtTime(ev.time)}`;
  return `Due at ${fmtTime(ev.time)}`;
}

function EventIcon({ type, tone }: { type: string; tone: Tone }) {
  if (tone === 'overdue') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#fef2f2]">
        <AlertTriangle className="h-[15px] w-[15px] text-[#dc2626]" />
      </div>
    );
  }
  if (type === 'session') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#fff4f0]">
        <Video className="h-[15px] w-[15px] text-[#ff5a1f]" />
      </div>
    );
  }
  if (type === 'contest') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#fff7ed]">
        <Trophy className="h-[15px] w-[15px] text-[#f59e0b]" />
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#f0f9ff]">
      <FileText className="h-[15px] w-[15px] text-[#0ea5e9]" />
    </div>
  );
}

function EventCard({ ev, tone, extra }: { ev: HomeTodayEvent; tone: Tone; extra: number }) {
  const href = eventHref(ev);
  const title = ev.title.replace(/^Session:\s*/i, '');

  const shell =
    `flex items-center gap-3 rounded-[12px] border px-[15px] py-[13px] transition-colors ${
      tone === 'overdue' ? 'border-[#fecaca] bg-[#fef7f7]' : 'border-[#f3f4f6] bg-[#fafafa]'
    }` +
    (href
      ? ` cursor-pointer ${tone === 'overdue' ? 'hover:border-[#fca5a5] hover:bg-[#fef2f2]' : 'hover:border-[#e5e7eb] hover:bg-white'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f]/40`
      : '');

  const body = (
    <>
      <EventIcon type={ev.type} tone={tone} />
      <div className="min-w-0">
        <p className="line-clamp-3 text-[13px] font-semibold leading-[19.5px] text-[#111827]">
          {title}
        </p>
        <p
          className={`text-[12px] font-medium leading-[17.5px] ${
            tone === 'overdue' ? 'text-[#dc2626]' : 'text-[#6b7280]'
          }`}
        >
          {subtitle(ev, tone)}
          {extra > 0 && (
            <span className="ml-1.5 inline-flex items-center rounded-full bg-[#f3f4f6] px-1.5 py-0.5 text-[10px] font-semibold text-[#6b7280]">
              +{extra} more
            </span>
          )}
        </p>
      </div>
    </>
  );

  // The trigger is focusable either way, so the detail is reachable by keyboard
  // even for rows that have nowhere to navigate.
  const trigger = href ? (
    <Link to={href as LinkProps['to']} className={shell}>
      {body}
    </Link>
  ) : (
    <div className={shell} tabIndex={0}>
      {body}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="right" align="start" className="max-w-[280px] space-y-1 py-2">
        <p className="text-[13px] font-semibold leading-snug">{title}</p>
        <p className="text-[11.5px] text-muted-foreground">
          {ev.batch_name ? `${kindLabel(ev)} · ${ev.batch_name}` : kindLabel(ev)}
        </p>
        <p className="text-[11.5px] text-muted-foreground">{fmtFull(ev.time)}</p>
        {/* For a plain session the status line just repeats the kind line above. */}
        {!(ev.type === 'session' && tone !== 'overdue') && (
          <p
            className={`text-[11.5px] font-medium ${
              tone === 'overdue' ? 'text-[#dc2626]' : 'text-[#111827]'
            }`}
          >
            {subtitle(ev, tone)}
          </p>
        )}
        {!href && (
          <p className="text-[11px] italic text-muted-foreground">No page to open for this item</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function toSlots(events: HomeTodayEvent[]) {
  return events.reduce<{ time: string; items: HomeTodayEvent[] }[]>((acc, ev) => {
    const last = acc[acc.length - 1];
    if (last && last.time === ev.time) {
      last.items.push(ev);
    } else {
      acc.push({ time: ev.time, items: [ev] });
    }
    return acc;
  }, []);
}

function SlotList({ slots, tone }: { slots: ReturnType<typeof toSlots>; tone: Tone }) {
  const last = slots.length - 1;
  // A rail is a sequence. With a single row there's nothing to connect, so the
  // dot would just be a decoration floating next to one card.
  const showRail = slots.length > 1;

  return (
    <div className="flex flex-col">
      {slots.map(({ time, items }, i) => {
        const ev = items[0];
        const extra = items.length - 1;
        const lead = i === 0 && tone !== 'upcoming';
        const [dayLine, timeLine] = gutterLines(time, tone);
        const accent =
          tone === 'overdue' ? 'text-[#dc2626]' : lead ? 'text-[#ff5a1f]' : 'text-[#9ca3af]';

        return (
          <div key={`${time}-${ev.id}`} className="flex items-stretch gap-4">
            {/* Time gutter */}
            <div className="w-16 shrink-0 pt-[5px] text-right">
              <span
                className={`block text-[12px] leading-[18px] tabular-nums ${lead || tone === 'overdue' ? 'font-semibold' : ''} ${accent}`}
              >
                {dayLine}
              </span>
              {timeLine && (
                <span className="block text-[11px] leading-[16px] tabular-nums text-[#9ca3af]">
                  {timeLine}
                </span>
              )}
            </div>

            {/* Rail: dot, then a connector that stretches to the next row */}
            {showRail && (
              <div className="flex shrink-0 flex-col items-center pt-[3px]">
                <span
                  className={`h-[10px] w-[10px] shrink-0 rounded-[5px] border-2 ${
                    tone === 'overdue'
                      ? 'border-[rgba(220,38,38,0.25)] bg-[#dc2626]'
                      : lead
                        ? 'border-[rgba(255,90,31,0.25)] bg-[#ff5a1f]'
                        : 'border-[#e5e7eb] bg-[#e5e7eb]'
                  }`}
                />
                {i !== last && (
                  <span className="w-[1.5px] flex-1 bg-[#f3f4f6]" aria-hidden="true" />
                )}
              </div>
            )}

            {/* Event card — a link when we know where it goes */}
            <div className={`min-w-0 flex-1 ${i !== last ? 'pb-5' : ''}`}>
              <EventCard ev={ev} tone={tone} extra={extra} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GroupLabel({ text, danger = false }: { text: string; danger?: boolean }) {
  return (
    <p
      className={`text-[10px] font-bold uppercase leading-[15px] tracking-[1.2px] ${
        danger ? 'text-[#dc2626]' : 'text-[#9ca3af]'
      }`}
    >
      {text}
    </p>
  );
}

export function TodayTimeline({
  todayEvents,
  upcomingEvents = [],
  dueItems = [],
}: {
  todayEvents: HomeTodayEvent[];
  /** Next few things after today, straight from the API — no client-side guessing. */
  upcomingEvents?: HomeTodayEvent[];
  dueItems?: HomeDueItem[];
}) {
  const overdueSlots = toSlots(overdueEvents(dueItems));
  const todaySlots = toSlots(todayEvents);
  const upcomingSlots = toSlots(upcomingEvents);

  const groups = [overdueSlots, todaySlots, upcomingSlots].filter((g) => g.length > 0);
  // Group labels are redundant when the section heading already names the only
  // group on screen.
  const labelled = groups.length > 1;

  const heading = todaySlots.length
    ? 'Today'
    : upcomingSlots.length
      ? 'Upcoming'
      : overdueSlots.length
        ? 'Overdue'
        : 'Upcoming';

  return (
    <TooltipProvider delayDuration={300}>
      <section aria-label={heading}>
        {/* Section heading sits outside the card, matching My Batches and
          Progress Snapshot so both dashboard columns start on the same line. */}
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-semibold leading-[24px] text-[#111827]">{heading}</h3>
          <Link
            to="/schedule"
            className="text-[13px] font-medium leading-[19.5px] text-[#ff5a1f] transition-opacity hover:opacity-75"
          >
            View all
          </Link>
        </div>

        <div className="mt-4 flex flex-col gap-5 rounded-[20px] border border-[#e7e7e7] bg-white p-[25px] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          {groups.length === 0 && (
            <p className="py-6 text-center text-[13px] text-[#9ca3af]">Nothing scheduled</p>
          )}

          {overdueSlots.length > 0 && (
            <div className="flex flex-col gap-3">
              {labelled && <GroupLabel text="Overdue" danger />}
              <SlotList slots={overdueSlots} tone="overdue" />
            </div>
          )}

          {todaySlots.length > 0 && (
            <div className="flex flex-col gap-3">
              {labelled && <GroupLabel text="Today" />}
              <SlotList slots={todaySlots} tone="today" />
            </div>
          )}

          {upcomingSlots.length > 0 && (
            <div className="flex flex-col gap-3">
              {labelled && <GroupLabel text="Upcoming" />}
              <SlotList slots={upcomingSlots} tone="upcoming" />
            </div>
          )}
        </div>
      </section>
    </TooltipProvider>
  );
}
